import * as React from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiClient, type Invoice, type InvoiceStatus } from "@alight/contracts";
import { Badge, Button, Card, CardContent } from "@alight/ui";
import { FileText, Printer, Send } from "lucide-react";
import { INVOICE_PRINT_CSS, InvoiceDocument } from "./InvoiceDocument";

/** Vite proxies `/api` to the Worker in dev; relative base is correct. */
const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

/**
 * Invoices — the family-level record of what was billed.
 *
 * One invoice covers the children a family registered together, which is the
 * same unit a grouped card payment settles. Line amounts are frozen at issue,
 * so this is what the family WAS charged, not what the program costs today.
 *
 * Read-only on purpose: an invoice is a record. Money moves on the
 * Reconciliation screen, and an invoice closes itself when every line's
 * enrolment is paid.
 */

const FILTERS: Array<{ label: string; value: InvoiceStatus | "all" }> = [
  { label: "All", value: "all" },
  { label: "Outstanding", value: "issued" },
  { label: "Paid", value: "paid" },
  { label: "Void", value: "void" },
];

function money(cents: number): string {
  return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(
    cents / 100,
  );
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

export function AdminInvoices() {
  const api = React.useMemo(() => apiClient(API_BASE), []);
  const [filter, setFilter] = React.useState<InvoiceStatus | "all">("all");
  const [printing, setPrinting] = React.useState<Invoice | null>(null);

  /* Bank details for the "how to pay" block on an outstanding invoice. Host-
     editable in Settings, served by the same public endpoint the pay page uses. */
  const payConfig = useQuery({
    queryKey: ["payment-config"],
    queryFn: () => api.paymentConfig(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  /* Print once the document has actually rendered — calling window.print() in
     the same tick as setState prints the page BEFORE the invoice exists, which
     is how the first attempt produced a near-empty sheet. */
  React.useEffect(() => {
    if (!printing) return;
    const done = () => setPrinting(null);
    window.addEventListener("afterprint", done, { once: true });
    const t = window.setTimeout(() => window.print(), 50);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("afterprint", done);
    };
  }, [printing]);

  const invoices = useQuery({
    queryKey: ["admin", "invoices", filter],
    queryFn: () => api.adminListInvoices(filter === "all" ? undefined : filter),
  });

  const rows = invoices.data ?? [];
  const outstanding = rows
    .filter((i) => i.status === "issued")
    .reduce((sum, i) => sum + i.total_cents, 0);

  return (
    <div className="space-y-5">
      <style>{INVOICE_PRINT_CSS}</style>
      {printing && <InvoiceDocument invoice={printing} bank={payConfig.data ?? null} />}
      <div>
        <h1 className="font-heading text-2xl font-extrabold text-ink">Invoices</h1>
        <p className="mt-1 text-sm text-muted">
          What each family was billed, and whether it has been settled. Amounts are frozen at
          issue — editing a program&apos;s pricing never changes an invoice already sent.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <Button
            key={f.value}
            size="sm"
            variant={filter === f.value ? "accent" : "outline"}
            onClick={() => setFilter(f.value)}
          >
            {f.label}
          </Button>
        ))}
        {outstanding > 0 && (
          <span className="ml-auto text-sm text-muted">
            Outstanding: <strong className="text-ink">{money(outstanding)}</strong>
          </span>
        )}
      </div>

      {invoices.isPending ? (
        <p className="text-sm text-muted">Loading invoices…</p>
      ) : invoices.isError ? (
        <p className="text-sm text-accent" role="alert">
          Couldn&apos;t load invoices. {String((invoices.error as Error)?.message ?? "")}
        </p>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="flex items-center gap-3 py-8 text-sm text-muted">
            <FileText className="h-5 w-5 shrink-0" aria-hidden />
            <span>
              No invoices yet. They are issued automatically when a family registers — older
              registrations predate invoicing and won&apos;t have one.
            </span>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {rows.map((inv) => (
            <InvoiceCard
              key={inv.id}
              invoice={inv}
              api={api}
              onPrint={() => setPrinting(inv)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function InvoiceCard({
  invoice,
  onPrint,
  api,
}: {
  invoice: Invoice;
  onPrint: () => void;
  api: ReturnType<typeof apiClient>;
}) {
  const [sent, setSent] = React.useState<string | null>(null);
  const send = useMutation({
    mutationFn: () => api.adminSendInvoice(invoice.id),
    onSuccess: (r) => setSent(r.sent_to),
  });

  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-semibold text-ink">{invoice.number}</span>
            <Badge
              variant={
                invoice.status === "paid"
                  ? "outline"
                  : invoice.status === "void"
                    ? "muted"
                    : "accent"
              }
            >
              {invoice.status === "issued" ? "Outstanding" : invoice.status}
            </Badge>
          </div>
          <span className="text-sm tabular-nums font-semibold text-ink">
            {money(invoice.total_cents)}
          </span>
        </div>

        <div className="text-sm text-muted">
          {invoice.parent_name ? `${invoice.parent_name} · ` : ""}
          {invoice.parent_email}
          {" · issued "}
          {shortDate(invoice.issued_at)}
          {invoice.paid_at ? ` · paid ${shortDate(invoice.paid_at)}` : ""}
          {invoice.payment_ref ? (
            <>
              {" · ref "}
              <span className="font-mono text-ink">{invoice.payment_ref}</span>
            </>
          ) : null}
        </div>

        <ul className="divide-y divide-border border-t border-border">
          {invoice.lines.map((line) => (
            <li key={line.id} className="flex items-baseline justify-between gap-3 py-1.5">
              <span className="text-sm text-ink">{line.description}</span>
              <span className="text-sm tabular-nums text-muted">{money(line.amount_cents)}</span>
            </li>
          ))}
        </ul>

        {/* Hidden on paper — an invoice does not have buttons on it. */}
        <div data-print-hide className="flex flex-wrap items-center gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onPrint}>
            <Printer className="h-4 w-4" aria-hidden />
            Print
          </Button>
          {/* A void invoice is a record of a bill that was withdrawn; sending
              it would ask for money nobody owes. */}
          {invoice.status !== "void" && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={send.isPending}
              onClick={() => send.mutate()}
            >
              <Send className="h-4 w-4" aria-hidden />
              {send.isPending ? "Sending…" : sent ? "Send again" : "Email to parent"}
            </Button>
          )}
          {sent && (
            <span role="status" className="text-xs font-medium text-success">
              Sent to {sent}
            </span>
          )}
          {send.isError && (
            <span role="alert" className="text-xs font-medium text-accent">
              Couldn&apos;t send — try again shortly.
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
